import { isMobile } from '../utils/device';

const enum DepthLayer {
  FAR = 0,
  MID = 1,
  NEAR = 2,
}

export interface FloatingElement {
  element: HTMLElement;
  x: number;
  y: number;
  speed: number;
  drift: number;
  driftPhase: number;
  driftSpeed: number;
  rotation: number;
  rotationSpeed: number;
  scale: number;
  type: string;
  depth: DepthLayer;
  life: number;
  maxLife: number;
  glowHue: number;
}

const MEME_ICONS: readonly { file: string; hue: number }[] = [
  { file: 'meme_doge_dog.svg', hue: 45 },
  { file: 'meme_surprised_shocked_pikachu.svg', hue: 55 },
  { file: 'meme_sad_frog.svg', hue: 130 },
  { file: 'meme_this_is_fine_dog.svg', hue: 20 },
  { file: 'meme_leonardo_dicaprio_laughing.svg', hue: 35 },
  { file: 'meme_roll_safe.svg', hue: 270 },
  { file: 'meme_salt_bae.svg', hue: 0 },
  { file: 'meme_polite_cat.svg', hue: 200 },
  { file: 'meme_handsome_squidward.svg', hue: 180 },
  { file: 'meme_patrick_i_have_3_dollars.svg', hue: 320 },
  { file: 'meme_homer_simpson_bushes.svg', hue: 60 },
  { file: 'meme_kermit_not_my_business.svg', hue: 140 },
] as const;

const CRYPTO_GLYPHS: readonly { content: string; color: string; hue: number }[] = [
  { content: '₿', color: '#f7931a', hue: 30 },
  { content: 'Ξ', color: '#627eea', hue: 230 },
  { content: '◎', color: '#14f195', hue: 155 },
  { content: '💎', color: '#a78bfa', hue: 260 },
  { content: '🚀', color: '#fbbf24', hue: 45 },
  { content: '🌙', color: '#a78bfa', hue: 265 },
] as const;

const MEME_WEIGHT = 0.7;

const LAYER_CONFIG = {
  [DepthLayer.FAR]:  { speedMin: 0.3, speedMax: 0.8, sizeMin: 32, sizeMax: 44, cssClass: 'depth-far' },
  [DepthLayer.MID]:  { speedMin: 0.8, speedMax: 1.6, sizeMin: 42, sizeMax: 58, cssClass: 'depth-mid' },
  [DepthLayer.NEAR]: { speedMin: 1.4, speedMax: 2.8, sizeMin: 56, sizeMax: 76, cssClass: 'depth-near' },
} as const;

const DEPTH_SCALE_VARS = {
  [DepthLayer.FAR]: { prop: '--meme-layer-far-scale', fallback: '0.4' },
  [DepthLayer.MID]: { prop: '--meme-layer-mid-scale', fallback: '0.65' },
  [DepthLayer.NEAR]: { prop: '--meme-layer-near-scale', fallback: '1' },
} as const;

export class FloatingElements {
  private container!: HTMLElement;
  private elements: FloatingElement[] = [];
  private animationId: number | null = null;
  private isRunning = false;
  private spawnTimer: number | null = null;
  private preloadedImages: Map<string, HTMLImageElement> = new Map();
  private containerH = 0;
  private containerW = 0;

  private readonly SPAWN_INTERVAL_MIN = 600;
  private readonly SPAWN_INTERVAL_MAX = 1600;
  private readonly MAX_ELEMENTS = 14;
  private readonly MAX_ELEMENTS_MOBILE = 6;
  private readonly ROTATION_SPEED_MIN = -0.015;
  private readonly ROTATION_SPEED_MAX = 0.015;

  constructor(containerId: string = 'floating-elements-container') {
    const container = document.getElementById(containerId) ||
      document.querySelector(`.${containerId}`) as HTMLElement;

    if (!container) return;

    this.container = container;
    this.preloadMemeIcons();
  }

  private preloadMemeIcons(): void {
    for (const icon of MEME_ICONS) {
      const img = new Image();
      img.src = `assets/memes/${icon.file}`;
      img.decoding = 'async';
      this.preloadedImages.set(icon.file, img);
    }
  }

  private pickDepthLayer(): DepthLayer {
    const r = Math.random();
    if (r < 0.35) return DepthLayer.FAR;
    if (r < 0.75) return DepthLayer.MID;
    return DepthLayer.NEAR;
  }

  private createElement(depth: DepthLayer): { el: HTMLElement; type: string; hue: number } {
    const cfg = LAYER_CONFIG[depth];
    const size = cfg.sizeMin + Math.random() * (cfg.sizeMax - cfg.sizeMin);
    const useMeme = Math.random() < MEME_WEIGHT;

    const wrapper = document.createElement('div');
    wrapper.className = `floating-element ${cfg.cssClass}`;
    wrapper.style.width = `${size}px`;
    wrapper.style.height = `${size}px`;

    let type: string;
    let hue: number;

    if (useMeme) {
      const icon = MEME_ICONS[Math.floor(Math.random() * MEME_ICONS.length)];
      type = icon.file;
      hue = icon.hue;

      const img = document.createElement('img');
      img.src = `assets/memes/${icon.file}`;
      img.alt = '';
      img.draggable = false;
      img.className = 'floating-meme-img';
      img.loading = 'lazy';
      img.decoding = 'async';
      wrapper.appendChild(img);
      wrapper.setAttribute('data-type', 'meme');
    } else {
      const glyph = CRYPTO_GLYPHS[Math.floor(Math.random() * CRYPTO_GLYPHS.length)];
      type = glyph.content;
      hue = glyph.hue;

      wrapper.textContent = glyph.content;
      wrapper.style.fontSize = `${size}px`;
      wrapper.style.lineHeight = '1';
      wrapper.style.color = glyph.color;
      wrapper.setAttribute('data-type', 'crypto');
    }

    return { el: wrapper, type, hue };
  }

  private spawnElement(): void {
    const maxElements = isMobile() ? this.MAX_ELEMENTS_MOBILE : this.MAX_ELEMENTS;
    if (this.elements.length >= maxElements) return;

    const depth = this.pickDepthLayer();
    const cfg = LAYER_CONFIG[depth];
    const { el, type, hue } = this.createElement(depth);

    this.container.appendChild(el);
    this.measureContainer();

    const startX = Math.random() * this.containerW;
    const startY = -70;
    const speed = cfg.speedMin + Math.random() * (cfg.speedMax - cfg.speedMin);
    const rotation = Math.random() * 360;
    const rotationSpeed = this.ROTATION_SPEED_MIN +
      Math.random() * (this.ROTATION_SPEED_MAX - this.ROTATION_SPEED_MIN);

    const depthVar = DEPTH_SCALE_VARS[depth];
    const scale = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue(depthVar.prop) || depthVar.fallback
    );

    const drift = 0.3 + Math.random() * 0.6;
    const driftSpeed = 0.005 + Math.random() * 0.01;
    const maxLife = (this.containerH + 140) / speed;

    el.style.left = `${startX}px`;
    el.style.top = `${startY}px`;
    el.style.setProperty('--glow-hue', String(hue));

    requestAnimationFrame(() => el.classList.add('active'));

    this.elements.push({
      element: el, x: startX, y: startY,
      speed, drift, driftPhase: Math.random() * Math.PI * 2, driftSpeed,
      rotation, rotationSpeed, scale, type,
      depth, life: 0, maxLife, glowHue: hue,
    });
  }

  private measureContainer(): void {
    const rect = this.container.getBoundingClientRect();
    this.containerH = rect.height;
    this.containerW = rect.width;
  }

  private update(): void {
    this.measureContainer();

    for (let i = this.elements.length - 1; i >= 0; i--) {
      const item = this.elements[i];
      item.life++;

      item.y += item.speed;

      item.driftPhase += item.driftSpeed;
      item.x += Math.sin(item.driftPhase) * item.drift;

      item.rotation += item.rotationSpeed * 60;

      const fadeIn = Math.min(1, item.life / 30);
      const remaining = item.maxLife - item.life;
      const fadeOut = Math.min(1, remaining / 60);
      const opacity = fadeIn * fadeOut;

      item.element.style.transform =
        `translate3d(${item.x}px, ${item.y}px, 0) rotate(${item.rotation}deg) scale(${item.scale})`;
      item.element.style.opacity = String(Math.max(0, opacity).toFixed(3));

      if (item.y > this.containerH + 80 || opacity <= 0) {
        item.element.remove();
        this.elements.splice(i, 1);
      }
    }
  }

  private animate = (): void => {
    if (!this.isRunning) return;
    this.update();
    this.animationId = requestAnimationFrame(this.animate);
  };

  private scheduleNextSpawn(): void {
    if (!this.isRunning) return;
    const interval = this.SPAWN_INTERVAL_MIN +
      Math.random() * (this.SPAWN_INTERVAL_MAX - this.SPAWN_INTERVAL_MIN);

    this.spawnTimer = window.setTimeout(() => {
      this.spawnElement();
      this.scheduleNextSpawn();
    }, interval);
  }

  public start(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.animate();
    this.scheduleNextSpawn();
    setTimeout(() => this.spawnElement(), 300);
  }

  public stop(): void {
    this.isRunning = false;
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    if (this.spawnTimer !== null) {
      clearTimeout(this.spawnTimer);
      this.spawnTimer = null;
    }
  }

  public clear(): void {
    this.stop();
    this.elements.forEach(item => item.element.remove());
    this.elements = [];
  }

  public destroy(): void {
    this.clear();
    this.preloadedImages.clear();
  }

  public setMaxElements(_max: number): void {}
}

